/**
 * <ss-collection> Web Component
 * Orchestrates the SearchSpring-powered collection page: reads
 * window.__searchspringConfig, calls the SS search API, and renders the product
 * grid with "View more" pagination (each click appends the next page into one
 * grid). Product cards are rendered client-side by <ss-product-card> directly
 * from the SS result JSON — no per-card Section Rendering API request.
 *
 * @module searchspring-collection
 */

import { searchSpringClient } from '@ts/searchspring-client';
import '@ts/searchspring-product-card';
import '@ts/searchspring-filters';
import {
  fetchResolverData,
  resolveFaceoutOverrides,
  resolveMessaging,
  resolvePrice,
  resolveSwatches,
} from '@ts/searchspring-swatches';
import { activeFaceoutRules, formatChain } from '@ts/faceout';
import { trackResults, trackProductClick } from '@ts/athos-beacon';
import { readGridDepth, saveGridDepthOnLeave } from '@ts/grid-state';

/** @type {function(): SearchSpringConfig} */
const config = () => window.__searchspringConfig || {};

const DEFAULT_PRODUCTS_PER_PAGE = 24;

/** Namespaces this list's stored back-navigation depth. */
const GRID_DEPTH_ID = 'ss-collection';

/** Coalesce rapid facet/sort toggles into a single search. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Parse a URL-supplied page number, clamped to a positive integer. NaN, zero,
 * and negatives all fall back to 1; the upper bound is enforced once the
 * response reports `totalPages`.
 * @param {string|null|undefined} value
 * @returns {number}
 */
const parsePageParam = (value) => Math.max(1, Number.parseInt(value || '', 10) || 1);

/**
 * Parse an inline-promo `filter` string into AND-conditions, one per line.
 * `"tags_style:pants,trousers\nvariant_size:m"` →
 *   [{ field: 'tags_style', values: ['pants', 'trousers'] }, { field: 'variant_size', values: ['m'] }]
 * @param {string|undefined} filterStr
 * @returns {{field: string, values: string[]}[]}
 */
const parseFilterConditions = (filterStr) => {
  if (!filterStr || typeof filterStr !== 'string') return [];
  return filterStr.split('\n').flatMap((line) => {
    const colon = line.indexOf(':');
    if (colon === -1) return [];
    const field = line.slice(0, colon).trim().toLowerCase();
    const values = line
      .slice(colon + 1)
      .split(',')
      .map((v) => v.trim().toLowerCase())
      .filter(Boolean);
    return field && values.length ? [{ field, values }] : [];
  });
};

/**
 * Specificity of a promo's filter against the active filters: the number of
 * matched AND-conditions, or -1 if it doesn't apply. Unfiltered promos return
 * -1. With `strict`, every active filter field must be one the promo declares.
 * @param {string|undefined} filterStr
 * @param {{field: string, value: string}[]} activeSummary - flattened active filters (lowercased)
 * @param {boolean|string|number|undefined} strict
 * @returns {number}
 */
const getFilterSpecificity = (filterStr, activeSummary, strict) => {
  const conditions = parseFilterConditions(filterStr);
  if (!conditions.length) return -1;

  const allMatch = conditions.every((condition) =>
    activeSummary.some((f) => f.field === condition.field && condition.values.indexOf(f.value) !== -1)
  );
  if (!allMatch) return -1;

  if (strict === true || strict === '1' || strict === 1) {
    const promoFields = conditions.map((c) => c.field);
    const noExtraFilters = activeSummary.every((f) => promoFields.indexOf(f.field) !== -1);
    if (!noExtraFilters) return -1;
  }

  return conditions.length;
};

/**
 * Keep the banners that apply to the current filter state, whatever their
 * placement. Pure: depends only on its arguments, so it can be unit-tested in
 * isolation.
 *
 * With no active filters, only unfiltered banners (no `filter`) show. Once any
 * filter is active, unfiltered banners hide and only the most filter-specific
 * matching banners remain; when nothing matches, nothing shows.
 *
 * @param {SSBannerConfig[]} banners
 * @param {{activeFilters?: Record<string, string[]>}} [state]
 * @returns {SSBannerConfig[]}
 */
export const filterPromoBanners = (banners, { activeFilters = {} } = {}) => {
  const validBanners = (banners || []).filter((b) => Boolean(b));
  if (!validBanners.length) return [];

  /** @type {{field: string, value: string}[]} */
  const activeSummary = [];
  for (const [field, values] of Object.entries(activeFilters)) {
    const f = String(field).trim().toLowerCase();
    for (const value of values) {
      activeSummary.push({ field: f, value: String(value).trim().toLowerCase() });
    }
  }
  if (!activeSummary.length) return validBanners.filter((b) => !b.filter);

  // Highest specificity present — lower-specificity banners are suppressed.
  let maxSpec = -1;
  for (const b of validBanners) {
    const spec = getFilterSpecificity(b.filter, activeSummary, b.filterStrict);
    if (spec > maxSpec) maxSpec = spec;
  }
  if (maxSpec < 1) return [];

  return validBanners.filter((b) => getFilterSpecificity(b.filter, activeSummary, b.filterStrict) === maxSpec);
};

/**
 * Resolve which slot-placed banners apply to the current filter state. Pure:
 * depends only on its arguments, so it can be unit-tested in isolation.
 *
 * Banners first pass {@link filterPromoBanners}, then group by placement slot
 * (`position`+`row`). When multiple banners tie for a slot (e.g. metafield
 * overrides move two promos onto the same row/position), a strict-filter
 * banner beats a loose one, otherwise the first in template order wins, and a
 * console warning names the collision.
 *
 * @param {SSBannerConfig[]} banners
 * @param {{activeFilters?: Record<string, string[]>}} [state]
 * @returns {SSBannerConfig[]}
 */
export const resolvePromoBanners = (banners, state = {}) => {
  const eligible = filterPromoBanners(banners, state);
  if (!eligible.length) return [];

  // Group by placement slot so conflicts resolve per slot, not globally.
  /** @type {Map<string, SSBannerConfig[]>} */
  const bySlot = new Map();
  for (const b of eligible) {
    const slot = `${b.position || 'left'}-${Math.max(1, Number(b.row) || 1)}`;
    if (!bySlot.has(slot)) bySlot.set(slot, []);
    (bySlot.get(slot) || []).push(b);
  }

  /** @type {SSBannerConfig[]} */
  const resolved = [];
  for (const [slot, slotBanners] of bySlot.entries()) {
    // Tiebreak: a strict promo is more precisely targeted, so it wins.
    const strictBanners = slotBanners.filter((b) => Boolean(b.filter && b.filterStrict));
    const candidates = strictBanners.length ? strictBanners : slotBanners;
    if (candidates.length > 1) {
      console.warn(
        `[CollectionGrid] ${candidates.length} grid promos target the same slot "${slot}"; showing the first and suppressing the rest.`
      );
    }
    const [chosen] = candidates;
    if (chosen) resolved.push(chosen);
  }

  return resolved;
};

/**
 * Read a promo tile's placement and targeting off the data-attributes of its
 * server-rendered `<template data-ss-grid-promo>` (see ts-plp-grid-promo).
 * @param {HTMLTemplateElement} tpl
 * @returns {SSBannerConfig}
 */
const parsePromoTemplate = (tpl) => ({
  tpl,
  placement: tpl.dataset.placement === 'row' ? 'row' : 'slot',
  row: Number(tpl.dataset.row) || 1,
  position: /** @type {'left' | 'middle' | 'right'} */ (tpl.dataset.position || 'left'),
  filter: tpl.dataset.filter || '',
  filterStrict: tpl.dataset.filterStrict === 'true',
  spanCols: Number(tpl.dataset.spanCols) || 2,
  spanRows: Number(tpl.dataset.spanRows) || 2,
});

class SSCollection extends HTMLElement {
  /** @type {AbortController|null} */
  #controller = null;
  /** @type {boolean} Guard against re-entrant search during render */
  #isRendering = false;

  /** @type {number} Last fetched page (grows as "View more" appends) */
  #currentPage = 1;
  /** @type {number} First loaded page (1 unless deep-linked via a legacy `page=` hash) */
  #firstLoadedPage = 1;
  /** @type {SSResult[]} Results accumulated across loaded pages */
  #allResults = [];
  /** @type {boolean} Guard against parallel next-page fetches */
  #isLoadingMore = false;
  /** @type {boolean} True while a full refresh (init/filter/sort/popstate) is in flight */
  #isRefreshing = false;
  /** @type {Record<string, string[]>} Active facet filters (set via URL hash; UI lands with the filter panel) */
  #activeFilters = {};
  /** @type {string} */
  #sortField = '';
  /** @type {string} */
  #sortDirection = '';

  /** @type {SSSearchResponse|null} */
  #response = null;

  /** @type {number|null} Desktop columns from the grid-density control; null = config default */
  #desktopColumns = null;
  /** @type {number|null} Mobile columns from the grid-density control; null = stylesheet default */
  #mobileColumns = null;

  /** @type {boolean} Guard against re-running promo insertion once per frame in the theme editor */
  #promoRefreshQueued = false;

  /** @type {ReturnType<typeof setTimeout> | null} Pending debounced search */
  #searchTimer = null;

  /** @type {number} Bumped per scheduled search, so a restore can detect being superseded */
  #searchGeneration = 0;

  /** @type {(() => void) | null} Unregisters the pagehide depth saver */
  #removeDepthSaver = null;

  /** @type {HTMLElement|null} Pre-paint depth placeholders (populated by the snippet's inline script) */
  #depthSkeleton = null;

  /** @type {number} Page depth a back-navigation restore is filling; 0 = none */
  #restoreTarget = 0;

  /**
   * Publishes the sticky launcher/chips bar's height (it varies with
   * promoted-pill wrapping and applied chips) so the desktop filter panel can
   * stick just below it.
   */
  #stickyHeaderResizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const height = Math.round(entry.borderBoxSize[0]?.blockSize ?? 0);
    this.style.setProperty('--ss-sticky-header-height', `${height}px`);
  });

  /** @type {(event: Event) => void} */
  #onLayoutChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const columns = Number(input.value);
    if (!columns) return;

    const viewport = input.name === 'ts-grid-mobile' ? 'mobile' : 'desktop';
    if (viewport === 'mobile') {
      this.#mobileColumns = columns;
    } else {
      this.#desktopColumns = columns;
    }
    this.#applyGridColumns();

    // Promo/featured tiles are placed with column-based grid areas computed for
    // the previous column count. Re-run their insertion so a 2×2 promo doesn't
    // overflow into an implicit track when the count shrinks (desktop 4→3, or
    // mobile 2→1).
    if (this.#response && this.#allResults.length) {
      this.#insertGridPromos();
    }

    // Best-effort: storage can be full or unavailable (e.g. theme-dev preview
    // throws QuotaExceededError), and the layout must still switch.
    try {
      sessionStorage.setItem(`ts-plp-columns-${viewport}`, String(columns));
    } catch {
      // noop
    }
  };

  /** Restore the layout choice persisted for this session and sync the radios. */
  #restoreLayout() {
    try {
      this.#desktopColumns = Number(sessionStorage.getItem('ts-plp-columns-desktop')) || null;
      this.#mobileColumns = Number(sessionStorage.getItem('ts-plp-columns-mobile')) || null;
    } catch {
      return;
    }

    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('[name="ts-grid"], [name="ts-grid-mobile"]')
    );
    for (const input of inputs) {
      const stored = input.name === 'ts-grid-mobile' ? this.#mobileColumns : this.#desktopColumns;
      if (stored) input.checked = Number(input.value) === stored;
    }
  }

  //#region - Lifecycle
  connectedCallback() {
    /** @type {HTMLTemplateElement | null} */
    this.productCardTemplate = this.querySelector('[data-ss-product-card-template]');
    /** @type {HTMLUListElement | null} */
    this.productGrid = this.querySelector('[data-ss-product-grid]');
    this.gridHeader = this.querySelector('[data-ss-grid-header]');
    this.stickyHeader = this.querySelector('[data-ss-sticky-header]');
    if (this.stickyHeader) this.#stickyHeaderResizeObserver.observe(this.stickyHeader);
    /** @type {HTMLElement | null} */
    this.gridResults = this.querySelector('[data-ss-grid-results]');
    /** @type {HTMLElement | null} Shared ancestor of skeleton + grid; owns the column vars */
    this.resultsWrapper = this.querySelector('[data-ss-results]');
    this.#depthSkeleton = this.querySelector('[data-ss-depth-skeleton]');
    this.emptyState = this.querySelector('[data-ss-empty-state]');

    // Filter & sort UI: launcher, panel, and the applied-filters row.
    this.filterLauncher = this.querySelector('[data-ss-filter-launcher]');
    this.filterPanel = this.querySelector('ss-filter-panel');
    this.appliedFilters = this.querySelector('[data-ss-applied-filters]');
    this.appliedFiltersList = this.querySelector('[data-ss-applied-filters-list]');
    /** @type {HTMLTemplateElement | null} */
    this.appliedChipTemplate = this.querySelector('[data-ss-applied-filter-chip-template]');
    this.clearAllButton = this.querySelector('[data-ss-clear-all]');

    // Clone the editable visual promoted-filters section (if present) into the
    // header slot so the wiring below picks up its cards.
    this.#insertPromotedFilters();

    // Promoted-filter controls (visual from the ts-promoted-filters section,
    // simple chips from custom.promoted_filters). Each applies a facet
    // field/value like a panel toggle; state syncs after search.
    this.promotedFilters = /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('[data-ss-promoted-filter]'))
    );
    for (const control of this.promotedFilters) {
      control.addEventListener('click', this.#onPromotedFilterClick);
    }
    // The slideshow hosting the visual cards treats any >=1px pointer movement
    // as a drag and takes pointer capture, retargeting the resulting click to
    // itself — so near-still clicks never reach the card's own listener.
    // Track the press point and recover those clicks at this level.
    this.addEventListener('pointerdown', this.#onPromotedFilterPointerDown);
    this.addEventListener('click', this.#recoverPromotedFilterClick);

    this.filterLauncher?.addEventListener('click', () => this.filterPanel?.toggle());
    this.clearAllButton?.addEventListener('click', () => this.#clearAllFilters());

    // Panel emits changes; <ss-collection> owns the filter/sort state + search.
    this.addEventListener('ss:panel:toggle', this.#onPanelToggle);
    this.addEventListener('ss:filter:toggle', this.#onFilterToggle);
    this.addEventListener('ss:sort:change', this.#onSortChange);
    this.appliedFiltersList?.addEventListener('click', this.#onChipClick);

    this.#restoreLayout();
    // Apply before the skeleton's first paint so a stored 1-up/3-up choice
    // doesn't flash the CSS fallback and relayout when the real grid reveals.
    this.#applyGridColumns();
    for (const input of this.querySelectorAll('[name="ts-grid"], [name="ts-grid-mobile"]')) {
      input.addEventListener('change', this.#onLayoutChange);
    }

    // Pagination: append the next page when the shopper clicks "View more".
    // #loadNextPage guards re-entry and termination (last page hides the button).
    this.loadMoreIndicator = this.querySelector('[data-ss-load-more]');
    /** @type {HTMLElement | null} */
    this.paginationContainer = this.querySelector('[data-ss-pagination]');
    /** @type {HTMLElement | null} */
    this.paginationRange = this.querySelector('[data-ss-pagination-range]');
    /** @type {HTMLButtonElement | null} */
    this.viewMoreButton = this.querySelector('[data-ss-view-more]');
    this.viewMoreButton?.addEventListener('click', () => this.#loadNextPage());

    // Beacon clickThrough for product links in the grid.
    this.productGrid?.addEventListener('click', (event) => trackProductClick(this.#beaconCategory(), event));

    // Persist the loaded "View more" depth for back-button restore (see
    // @ts/grid-state). Depth is only meaningful when loading started from page
    // 1 — a legacy `page=` deep link keeps today's behavior.
    this.#removeDepthSaver = saveGridDepthOnLeave(GRID_DEPTH_ID, () =>
      this.#firstLoadedPage === 1 ? this.#currentPage : null
    );

    this.#init();

    window.addEventListener('popstate', this.#handlePopState);

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.#onDesignModeSectionUpdate);
      document.addEventListener('shopify:section:unload', this.#onDesignModeSectionUpdate);
    }
  }

  disconnectedCallback() {
    this.#removeDepthSaver?.();
    this.#controller?.abort();
    if (this.#searchTimer) clearTimeout(this.#searchTimer);
    this.#stickyHeaderResizeObserver.disconnect();
    window.removeEventListener('popstate', this.#handlePopState);
    document.removeEventListener('shopify:section:load', this.#onDesignModeSectionUpdate);
    document.removeEventListener('shopify:section:unload', this.#onDesignModeSectionUpdate);
  }
  //#endregion

  /**
   * A search page with no query would otherwise fetch with neither `q` nor a
   * collection filter, returning the entire catalog. Detect that case so we can
   * show the empty state instead.
   * @returns {boolean}
   */
  #isBlankSearch() {
    const cfg = config();
    return cfg.pageType === 'search' && !(cfg.searchQuery || '').trim();
  }

  /** Set up everything required for first load. */
  async #init() {
    this.#parseHash();
    this.#firstLoadedPage = this.#currentPage;

    if (this.#isBlankSearch()) {
      this.#hideLoadingGrid();
      this.#clearDepthSkeleton();
      this.productGrid?.classList.add('ss-hidden');
      this.emptyState?.classList.remove('ss-hidden');
      return;
    }

    // Returning via the back button: resume the "View more" depth the shopper
    // had reached. Only meaningful from page 1 — a `page=` deep link is already
    // showing a specific page.
    const savedDepth = this.#firstLoadedPage === 1 ? readGridDepth(GRID_DEPTH_ID) : null;
    this.#restoreTarget = savedDepth || 0;

    this.#isRefreshing = true;
    const requestStatus = await this.#fetchFirstPage();

    if (requestStatus === 'success') {
      this.#allResults = this.#response?.results || [];
      await this.#renderProducts();
      this.#syncFilterUI();
      this.#hideLoadingGrid();
    } else if (requestStatus === 'failure') {
      // Minimal failure UI: drop the skeleton and reveal the server-rendered
      // first page (grid + next-page link) when present — a better fallback
      // than "No products found" — otherwise surface the empty state.
      this.#hideLoadingGrid();
      this.#clearDepthSkeleton();
      if (this.#hasServerRendered()) {
        this.productGrid?.classList.remove('ss-hidden');
        for (const node of this.querySelectorAll('[data-ss-ssr]')) node.classList.remove('ss-hidden');
      } else {
        this.productGrid?.classList.add('ss-hidden');
        this.emptyState?.classList.remove('ss-hidden');
      }
    } else {
      // 'aborted': a superseding search owns the UI and already cleared the
      // placeholders — this is defense for any future abort path that doesn't.
      this.#clearDepthSkeleton();
    }
    this.#isRefreshing = false;
    document.dispatchEvent(new CustomEvent('ts:searchspring:dom:ready'));

    // Appends pages the same way a "View more" click does, so it runs after
    // dom:ready rather than holding the event back.
    if (savedDepth && requestStatus === 'success') await this.#restoreDepth(savedDepth);
  }

  /**
   * Re-append the pages the shopper had loaded before navigating away, through
   * the same path a "View more" click takes. Once the grid is back to its
   * previous height the browser's scroll restoration lands the position.
   *
   * @param {number} depth - Page depth to restore to.
   */
  async #restoreDepth(depth) {
    const generation = this.#searchGeneration;

    try {
      while (this.#currentPage < depth && this.#hasMorePages()) {
        const page = this.#currentPage;
        await this.#loadNextPage();

        // Page unchanged means the fetch failed (or was refused) — stop instead
        // of retrying forever. A new search supersedes the restore entirely.
        if (this.#currentPage === page || this.#searchGeneration !== generation) break;
      }
    } finally {
      // Placeholders must not outlive the restore, however it ended.
      this.#clearDepthSkeleton();
    }
  }

  /**
   * Execute the search API call.
   * @returns {Promise<'success' | 'failure' | 'aborted'>}
   */
  async fetchSearchResults() {
    // Cancel any in-flight request before starting a new one.
    this.#controller?.abort();
    this.#controller = new AbortController();

    const cfg = config();

    // Search pages query by keyword; collection pages background-filter by
    // collection id. The two are mutually exclusive.
    const isSearch = cfg.pageType === 'search';
    // A blank term stays undefined so no `q` is sent (avoids an empty-keyword query).
    const query = isSearch ? (cfg.searchQuery || '').trim() || undefined : undefined;

    /** @type {Record<string, string>} */
    const bgFilters = {};
    if (!isSearch && cfg.collectionId) {
      bgFilters.collection_id = String(cfg.collectionId);
    }

    try {
      this.#response = await searchSpringClient.search({
        q: query,
        page: this.#currentPage,
        resultsPerPage: cfg.resultsPerPage || DEFAULT_PRODUCTS_PER_PAGE,
        filters: this.#activeFilters,
        bgFilters,
        sortField: this.#sortField,
        sortDirection: this.#sortDirection,
        controller: this.#controller,
      });
      return 'success';
    } catch (err) {
      if (/** @type {Error} */ (err).name === 'AbortError') {
        return 'aborted';
      }
      console.error('[SearchSpring] Search failed:', err);
      return 'failure';
    }
  }

  /**
   * Fetch the first loaded page, reconciling a URL-supplied page number against
   * the response. `?page=` is crawlable (and therefore hostile) input: a value
   * past the last page returns `totalResults > 0` with no results, which would
   * render a blank grid under a non-zero count. Clamp to the last page and
   * fetch once more.
   * @returns {Promise<'success' | 'failure' | 'aborted'>}
   */
  async #fetchFirstPage() {
    const status = await this.fetchSearchResults();
    if (status !== 'success') return status;

    const totalPages = this.#response?.pagination?.totalPages || 0;
    if (totalPages > 0 && this.#currentPage > totalPages) {
      this.#currentPage = totalPages;
      this.#firstLoadedPage = totalPages;
      return this.fetchSearchResults();
    }
    return status;
  }

  #handlePopState = async () => {
    // Supersedes an in-flight back-navigation restore (see #restoreDepth); its
    // placeholders must not wait on this search's success to be cleaned up.
    this.#searchGeneration += 1;
    this.#clearDepthSkeleton();
    this.#parseHash();
    this.#firstLoadedPage = this.#currentPage;

    this.#isRefreshing = true;
    const status = await this.#fetchFirstPage();
    if (status === 'success') {
      this.#allResults = this.#response?.results || [];
      await this.#renderProducts();
      this.#syncFilterUI();
    }
    this.#isRefreshing = false;
  };

  /** @returns {'search'|'category'} Beacon event category for this page type. */
  #beaconCategory() {
    return config().pageType === 'search' ? 'search' : 'category';
  }

  #createProductCard(/** @type {number} */ index) {
    if (!this.productCardTemplate) {
      return document.createDocumentFragment();
    }
    const fragment = document.importNode(this.productCardTemplate.content, true);
    const productCard = /** @type {HTMLElement} */ (fragment.querySelector('[data-ss-product-card]'));
    if (!productCard) {
      return document.createDocumentFragment();
    }
    productCard.dataset.index = String(index);
    return fragment;
  }

  #hideLoadingGrid() {
    this.querySelector('.ss-skeleton')?.classList.add('ss-hidden');
  }

  //#region - Server-rendered first page
  /**
   * The Liquid snippet ships the first page of collection products (cards and
   * a `?page=N` next link) marked `data-ss-ssr` so the HTML is crawlable. They
   * stay `ss-hidden` behind the skeleton — Searchspring's merchandised order
   * differs from Shopify's, and swapping visible cards was jarring — and the
   * first successful render replaces them with `<ss-product-card>`s. Only a
   * failed request reveals them (see #init).
   * @returns {boolean}
   */
  #hasServerRendered() {
    return Boolean(this.querySelector('[data-ss-ssr]'));
  }

  #removeServerRendered() {
    for (const node of this.querySelectorAll('[data-ss-ssr]')) node.remove();
  }
  //#endregion

  #showLoadingGrid() {
    this.querySelector('.ss-skeleton')?.classList.remove('ss-hidden');
  }

  /**
   * The snippet's inline script sizes [data-ss-depth-skeleton] to the stored
   * back-navigation depth during parse, so the browser's scroll restoration
   * sees the grid's restored height before any results arrive. Each render
   * trims placeholders from the front — the grid grows above by the same
   * amount, so the survivors keep their document offset and the total height
   * never dips below the restored scroll position mid-restore.
   */
  #trimDepthSkeleton() {
    if (!this.#depthSkeleton) return;

    const perPage = config().resultsPerPage || DEFAULT_PRODUCTS_PER_PAGE;
    const totalResults = this.#response?.pagination?.totalResults || 0;
    const wanted = this.#restoreTarget
      ? Math.min(this.#restoreTarget * perPage, totalResults) - this.#allResults.length
      : 0;

    if (wanted <= 0) {
      this.#clearDepthSkeleton();
      return;
    }
    while (this.#depthSkeleton.childElementCount > wanted) {
      this.#depthSkeleton.firstElementChild?.remove();
    }
  }

  #clearDepthSkeleton() {
    this.#restoreTarget = 0;
    this.#depthSkeleton?.remove();
    this.#depthSkeleton = null;
  }

  /** Column counts for the current density choice (stored → config → default). */
  #gridColumns() {
    return {
      desktop: this.#desktopColumns || config().productsPerRow || 4,
      mobile: this.#mobileColumns || 2,
    };
  }

  #applyGridColumns() {
    // Set once on the results wrapper: the skeleton, the product grid and the
    // "View more" shimmer all inherit the vars, so the swap from placeholder to
    // product grid doesn't shift layout.
    const target = this.resultsWrapper;
    if (!target) return;
    // minmax(0, 1fr): iOS WebKit reuses the previous layout's resolved track
    // size when a repeat() column count changes through a custom property
    // (1-up restored from storage → 2-up leaves both tracks viewport-wide);
    // a zero minimum forces the tracks to re-resolve against free space.
    const { desktop, mobile } = this.#gridColumns();
    target.style.setProperty('--product-grid-columns-desktop', `repeat(${desktop}, minmax(0, 1fr))`);
    if (this.#mobileColumns) {
      target.style.setProperty('--product-grid-columns-mobile', `repeat(${mobile}, minmax(0, 1fr))`);
    }
  }

  #renderGrid() {
    if (!this.#response || !this.productGrid || !this.gridResults) {
      return;
    }
    const products = this.#allResults;

    this.productGrid.className = 'ss-product-grid product-grid';
    this.productGrid.setAttribute('role', 'list');
    this.#applyGridColumns();

    // Server-rendered placeholders are replaced wholesale by the live results.
    this.#removeServerRendered();

    // On re-renders (filter toggle, page change) the grid already holds the
    // previous render's <li>s — reuse them (trim extras, re-index) so cards are
    // re-populated via setResult instead of being torn down and recreated. On
    // first render the grid holds only server-rendered placeholders (removed
    // above; live cards live in a <template>), so this is empty and we fall
    // through to replaceChildren below. Injected promo tiles are not product
    // slots, so they're excluded here (#insertGridPromos owns their lifecycle).
    const existingItems = Array.from(
      this.productGrid.querySelectorAll('li.product-grid__item:not(.ts-plp-grid-promo)')
    );
    if (existingItems.length) {
      if (existingItems.length > products.length) {
        existingItems.slice(products.length).forEach((el) => el.remove());
      }
      for (let i = 0; i < products.length; i++) {
        const existing = existingItems[i];
        if (existing) {
          const card = existing.querySelector('ss-product-card');
          if (card) card.dataset.index = String(i);
        } else {
          this.productGrid.appendChild(this.#createProductCard(i));
        }
      }
    } else {
      const productFragments = products.map((_, i) => this.#createProductCard(i));
      this.productGrid.replaceChildren(...productFragments);
    }

    if (products.length) {
      this.#insertGridPromos();
    }

    const totalResults = this.#response.pagination?.totalResults || 0;
    const countTemplate =
      totalResults === 1 ? this.gridResults.dataset.resultCountOne : this.gridResults.dataset.resultCountOther;
    this.gridResults.textContent = (countTemplate || '[count] results').replace('[count]', String(totalResults));

    const isEmpty = totalResults === 0;
    this.productGrid.classList.toggle('ss-hidden', isEmpty);
    this.emptyState?.classList.toggle('ss-hidden', !isEmpty);

    this.#trimDepthSkeleton();
    this.#updatePaginationUI();
  }

  //#region - Grid promos
  /**
   * The theme editor re-renders a section's markup in place, but a grid promo
   * reaches the product grid as a clone of that section's inert `<template>` —
   * the clone doesn't follow the re-render, so edits only surfaced once a
   * filter, sort, or layout change forced a full render. Re-clone on every
   * section load/unload instead. Design mode only.
   * @type {() => void}
   */
  #onDesignModeSectionUpdate = () => {
    if (this.#promoRefreshQueued) return;
    this.#promoRefreshQueued = true;
    // An unloading section is still in the DOM when the event fires; wait a
    // frame so the document-wide template query sees the settled markup.
    requestAnimationFrame(() => {
      this.#promoRefreshQueued = false;
      if (this.#response && this.#allResults.length) this.#insertGridPromos();
    });
  };

  /**
   * Insert promo tiles into the grid from the unified
   * `<template data-ss-grid-promo>` contract. Each promo section emits one inert
   * template (via the shared ts-plp-grid-promo snippet) carrying its placement on
   * data-attributes; this reads them and injects the winners. Templates are found
   * document-wide, so a section's DOM position is irrelevant.
   *
   * Every promo first passes the filter gate ({@link filterPromoBanners}),
   * decided across all placements at once: with no filters active only
   * unfiltered promos show; once a filter is applied they hide and only the
   * most filter-specific matching promos remain.
   *
   * Two placement modes, keyed on `data-placement`:
   *  - `slot`: explicit row/position (e.g. the 2×2 inline promo). One banner
   *    wins each slot ({@link resolvePromoBanners}); placed via
   *    `--ts-promo-col-start`/`--ts-promo-row-start`, products backfill via
   *    `grid-auto-flow: dense`.
   *  - `row`: full-width tiles pinned to an explicit grid row
   *    ({@link SSCollection.#insertRowPromos}). A tile whose row is already
   *    taken (by a slot tile or an earlier row tile) slides down to the next
   *    free row instead of overlapping; one pinned past the loaded products
   *    waits for the page that fills its row.
   *
   * Re-runs on every full render (filter/sort/page) and on layout change.
   */
  #insertGridPromos() {
    const grid = this.productGrid;
    if (!grid) return;

    // Clear injected promos from a previous render to avoid duplicates.
    for (const el of grid.querySelectorAll('.ts-plp-grid-promo')) {
      el.remove();
    }

    const templates = /** @type {HTMLTemplateElement[]} */ (
      Array.from(document.querySelectorAll('template[data-ss-grid-promo]'))
    );
    if (!templates.length) return;

    const banners = filterPromoBanners(templates.map(parsePromoTemplate), { activeFilters: this.#activeFilters });
    const slotBanners = banners.filter((b) => b.placement !== 'row');
    const rowBanners = banners.filter((b) => b.placement === 'row');

    // Slot tiles keep their configured rows; row tiles yield around them.
    const slots = slotBanners.length ? this.#insertSlotPromos(slotBanners) : { rows: new Set(), cells: 0 };
    if (rowBanners.length) this.#insertRowPromos(rowBanners, slots.rows, slots.cells);
  }

  /**
   * Insert slot-placed promo tiles (explicit row/position — e.g. the 2×2
   * inline promo). One banner is shown per placement slot; the rest at that
   * slot are suppressed.
   * @param {SSBannerConfig[]} banners - Filter-eligible slot promos, each with its `tpl`.
   * @returns {{rows: Set<number>, cells: number}} `rows`: the grid rows the
   *   inserted tiles occupy (each tile covers its start row through `spanRows`
   *   rows), so row-placed full-width tiles can avoid them. `cells`: the grid
   *   cells they take from products, so row-placed tiles can tell how far the
   *   loaded products fill the grid.
   */
  #insertSlotPromos(banners) {
    /** @type {Set<number>} */
    const occupiedRows = new Set();
    let occupiedCells = 0;
    const grid = this.productGrid;
    if (!grid) return { rows: occupiedRows, cells: occupiedCells };

    const cols = this.#desktopColumns || config().productsPerRow || 4;
    /** @type {Record<string, number>} */
    const colStartByPosition = { left: 1, middle: 2, right: 3 };

    // `row` is the 1-based grid row the tile starts on; it spans that row + the
    // next. With load-more pagination all results share one grid, so rows are
    // absolute — there is no per-page targeting.
    /** @type {{banner: SSBannerConfig, rowStart: number}[]} */
    const placed = this.#resolvePromoBanners(banners).map((banner) => ({
      banner,
      rowStart: Math.max(1, Number(banner.row) || 1),
    }));
    if (!placed.length) return { rows: occupiedRows, cells: occupiedCells };

    for (const { banner, rowStart } of placed) {
      if (!banner.tpl) continue;

      const fragment = /** @type {DocumentFragment} */ (banner.tpl.content.cloneNode(true));
      const li = /** @type {HTMLElement|null} */ (fragment.querySelector('.ts-plp-grid-promo'));
      if (!li) continue;

      const spanRows = Math.max(1, Number(banner.spanRows) || 2);
      for (let i = 0; i < spanRows; i++) {
        occupiedRows.add(rowStart + i);
      }
      occupiedCells += spanRows * Math.max(1, Number(banner.spanCols) || 2);

      // The tile spans `spanCols` columns, so its start must leave room for the
      // rest — clamp so a `right` slot can't overflow into an implicit track when
      // the grid has fewer columns (e.g. 3-up).
      const span = Math.max(1, Number(banner.spanCols) || 2);
      const colStart = Math.min(colStartByPosition[banner.position || 'left'] || 1, Math.max(1, cols - (span - 1)));
      li.style.setProperty('--ts-promo-col-start', String(colStart));
      li.style.setProperty('--ts-promo-row-start', String(rowStart));

      // Sides flush with the grid edge bleed off the screen (the grid is
      // full-bleed) and stay gapless; inward-facing sides get a gap in CSS.
      if (colStart === 1) li.setAttribute('data-grid-edge-start', '');
      if (colStart + span - 1 >= cols) li.setAttribute('data-grid-edge-end', '');

      // Insert near the tile's visual position so the reading order tracks it.
      const cellIndex = (rowStart - 1) * cols + (colStart - 1);
      const ref = grid.children[Math.min(cellIndex, grid.children.length)];
      grid.insertBefore(li, ref || null);
      this.#loadPromoVideos(li);
    }

    return { rows: occupiedRows, cells: occupiedCells };
  }

  /**
   * Resolve which configured banners apply to the current filter state.
   * Thin wrapper over the pure {@link resolvePromoBanners}.
   * @param {SSBannerConfig[]} banners
   * @returns {SSBannerConfig[]}
   */
  #resolvePromoBanners(banners) {
    return resolvePromoBanners(banners, {
      activeFilters: this.#activeFilters,
    });
  }

  /**
   * Insert full-width row-placed promo tiles. Each tile spans the whole row it
   * is pinned to (`--ts-promo-row-start`, desktop); products backfill around it
   * via `grid-auto-flow: dense`. Rows are absolute across the accumulated
   * load-more grid. Because a full-width tile owns every column of its row, a
   * tile whose requested row is already taken — by a slot tile or by an
   * earlier row tile (template order) — slides down to the next free row
   * instead of overlapping.
   *
   * A row past the end of the loaded products waits for the pages that fill it
   * (this re-runs on every load-more): pinning it now would leave a stack of
   * empty implicit rows — each still paying the grid's row-gap — and float the
   * tile above the products meant to precede it. Once the last page is in, an
   * unreachable row clamps to the end of the grid instead so the promo still
   * shows.
   * @param {SSBannerConfig[]} banners - Filter-eligible row promos, each with its `tpl`.
   * @param {Set<number>} [occupiedRows] - Rows already covered by slot tiles.
   * @param {number} [occupiedCells] - Grid cells slot tiles take from products.
   */
  #insertRowPromos(banners, occupiedRows = new Set(), occupiedCells = 0) {
    const grid = this.productGrid;
    if (!grid) return;

    const cols = this.#desktopColumns || config().productsPerRow || 4;
    const claimedRows = new Set(occupiedRows);
    // Cells the grid has content for; each tile placed below pushes a row of
    // products down, so it adds a row's worth.
    let filledCells = this.#allResults.length + occupiedCells;
    const morePagesToLoad = this.#hasMorePages();

    for (const { tpl, row } of banners) {
      if (!tpl) continue;

      // ts-plp-grid-promo guarantees data-row is a whole number ≥ 1 (it coerces
      // the setting and emits no template without a valid row); the fallback
      // here is purely defensive and never fires for well-formed templates.
      const requestedRow = Math.max(1, Number(row) || 1);
      // One row past the filled rows is still adjacent to the last product, so
      // it places cleanly — anything beyond that is a gap.
      const lastPlaceableRow = Math.ceil(filledCells / cols) + 1;

      let rowStart = requestedRow;
      if (rowStart > lastPlaceableRow) {
        if (morePagesToLoad) continue;
        console.warn(
          `[CollectionGrid] Grid promo row ${requestedRow} is past the end of the grid; placing a full-width promo at row ${lastPlaceableRow}.`
        );
        rowStart = lastPlaceableRow;
      }

      const targetRow = rowStart;
      // Bounded: each iteration steps past a claimed row, and claimedRows only
      // holds the handful of rows covered by slot tiles + earlier row promos.
      while (claimedRows.has(rowStart)) rowStart++;
      if (rowStart !== targetRow) {
        console.warn(
          `[CollectionGrid] Grid promo row ${targetRow} is already occupied; moving a full-width promo down to row ${rowStart}.`
        );
      }

      const fragment = /** @type {DocumentFragment} */ (tpl.content.cloneNode(true));
      const li = /** @type {HTMLElement|null} */ (fragment.querySelector('.ts-plp-grid-promo'));
      if (!li) continue;

      claimedRows.add(rowStart);
      filledCells += cols;
      li.style.setProperty('--ts-promo-row-start', String(rowStart));

      // Insert near the tile's visual position so the reading order tracks it
      // (and mobile, which has no explicit row placement, lands close by).
      const cellIndex = (rowStart - 1) * cols;
      const ref = grid.children[Math.min(cellIndex, grid.children.length)];
      grid.insertBefore(li, ref || null);
      this.#loadPromoVideos(li);
    }
  }

  /**
   * Start loading the videos in a promo tile that was just inserted. The tile
   * is a clone of an inert `<template>`, and WebKit (every iOS browser) never
   * runs resource selection for a `<video>` cloned that way: it stays at
   * NETWORK_EMPTY with only its poster painted, so `autoplay` never fires.
   * Chrome loads the clone anyway, which is why this only broke on iOS.
   * `load()` starts it explicitly (as Horizon's video-background.js does on
   * connect) and `autoplay` takes over from there. Vimeo embeds are iframes,
   * which aren't affected.
   * @param {HTMLElement} tile - The promo `<li>`, already in the grid.
   */
  #loadPromoVideos(tile) {
    for (const video of tile.querySelectorAll('video')) {
      video.load();
    }
  }
  //#endregion

  //#region - Pagination
  /** @returns {boolean} True when more result pages remain to load. */
  #hasMorePages() {
    const totalPages = this.#response?.pagination?.totalPages || 1;
    return this.#currentPage < totalPages;
  }

  /**
   * Fetch and append the next result page. Triggered by the "View more"
   * button; re-entry is guarded and reaching the last page terminates quietly.
   * On failure the page is stepped back so the next click retries it.
   */
  async #loadNextPage() {
    if (this.#isLoadingMore || this.#isRefreshing || this.#isRendering) return;
    if (!this.#response || !this.#hasMorePages()) return;

    this.#isLoadingMore = true;
    if (this.viewMoreButton) this.viewMoreButton.disabled = true;
    // During a depth restore the placeholders are the loading indicator.
    if (!this.#depthSkeleton) this.loadMoreIndicator?.classList.remove('ss-hidden');

    this.#currentPage += 1;
    const status = await this.fetchSearchResults();

    if (status === 'success') {
      this.#allResults = this.#allResults.concat(this.#response?.results || []);
      await this.#renderProducts();
    } else if (status === 'failure') {
      // Step back so the next click retries this page instead of skipping it.
      this.#currentPage -= 1;
    }
    // 'aborted' means a filter/sort refresh superseded this load — that flow
    // already reset the page state, so there is nothing to undo here.

    this.loadMoreIndicator?.classList.add('ss-hidden');
    if (this.viewMoreButton) this.viewMoreButton.disabled = false;
    this.#isLoadingMore = false;
  }

  /**
   * Sync the "View more" button label and range count ("1-24 of 96 products")
   * to the loaded results. Hides the button on the last page and the whole
   * footer when there are no results.
   */
  #updatePaginationUI() {
    if (!this.paginationContainer) return;

    const total = this.#response?.pagination?.totalResults || 0;
    this.paginationContainer.classList.toggle('ss-hidden', total === 0);
    if (total === 0) return;

    const perPage = config().resultsPerPage || DEFAULT_PRODUCTS_PER_PAGE;
    const first = (this.#firstLoadedPage - 1) * perPage + 1;
    const last = first + this.#allResults.length - 1;

    if (this.paginationRange) {
      this.paginationRange.textContent = (this.paginationRange.dataset.rangeTemplate || '[first]-[last] of [count]')
        .replace('[first]', String(first))
        .replace('[last]', String(last))
        .replace('[count]', String(total));
    }

    if (this.viewMoreButton) {
      const hasMore = this.#hasMorePages();
      this.viewMoreButton.classList.toggle('ss-hidden', !hasMore);
      if (hasMore) {
        this.viewMoreButton.textContent = (this.viewMoreButton.dataset.viewMoreTemplate || 'View [count] more').replace(
          '[count]',
          String(Math.min(perPage, total - last))
        );
      }
    }
  }
  //#endregion

  async #renderProducts() {
    if (!this.#response || this.#isRendering) return;

    this.#isRendering = true;

    // Expose the page's faceout rule chains ("lead,hover", rules joined by
    // ">", e.g. "model>runway:3,2"; see @ts/faceout) for QA and analytics.
    const faceout = activeFaceoutRules();
    this.dataset.tsFaceout = `${formatChain(faceout.primary)},${formatChain(faceout.secondary)}`;

    const products = this.#allResults;
    this.#renderGrid();

    // Every "View more" append re-runs this render over the whole accumulated
    // list. Earlier pages carry over by reference, so a card already holding
    // its result is left untouched — its swatch strip and any colorway the
    // shopper selected survive the append. Only new cards (or all of them,
    // after a filter/sort refresh swaps the result set) render and enter
    // swatch hydration.
    const cards = Array.from(this.querySelectorAll('ss-product-card'));
    // Computed once per batch (not per card): cards use it for `sizes` and the
    // eager-row cutoff without a getComputedStyle per render.
    const columns = this.#gridColumns();
    /** @type {SSProductCardElement[]} */
    const freshCards = [];
    /** @type {SSResult[]} */
    const freshProducts = [];
    for (const [i, card] of cards.entries()) {
      const product = products[i];
      if (!product || card.getResult() === product) continue;
      card.setResult(product, i, columns);
      freshCards.push(card);
      freshProducts.push(product);
    }

    this.setAttribute('data-ss-hydrated', 'true');
    this.#isRendering = false;

    // Beacon render + impression for the newly rendered results. "View more"
    // appends report only the appended page under that page's responseId and
    // extend the attribution context; a full re-render (filter/sort/popstate,
    // including a zero-result one) replaces it.
    if (this.#response.responseId) {
      trackResults(this.#beaconCategory(), this.#response.responseId, freshProducts, {
        append: freshProducts.length < products.length,
      });
    }

    // Swatches, live prices and live messaging hydrate after the cards paint —
    // a section-render fetch resolves each card's colorway group, presentment
    // price and messaging. Non-blocking; failures leave cards swatch-less on
    // approximate prices, with the feed's messaging.
    this.#hydrateResolverData(freshProducts, freshCards);
  }

  /**
   * Resolve colorway swatches, live presentment prices and live messaging for
   * newly rendered cards: groups (siblings + their commerce data), each
   * product's own Markets-rounded price and its messaging come
   * server-rendered from live store data via the
   * ts-swatch-resolver section, one section-render fetch per batch of new
   * product ids (see ts-searchspring-swatches.js).
   *
   * Deliberately NOT tied to the page-fetch AbortController — fetching the
   * next page would cancel it, and these cards are only
   * hydrated once. A late response can't go stale: a card is skipped when a
   * refresh reassigned it to a different result while the query ran.
   * @param {SSResult[]} products
   * @param {SSProductCardElement[]} cards - Parallel to `products`.
   */
  async #hydrateResolverData(products, cards) {
    if (!products.length) return;

    /** @type {Map<string, TSResolverEntry>|null} */
    let entries = null;
    try {
      entries = await fetchResolverData(products);
    } catch (err) {
      console.error('[CollectionGrid] Resolver hydration failed:', err);
    }

    for (const [i, card] of cards.entries()) {
      const product = products[i];
      if (!product || card.getResult() !== product) continue;
      if (entries) card.setSwatches(resolveSwatches(product, entries));
      // Non-USD cards defer their price paint to this call; a null price
      // falls back to the approximate feed-converted amount.
      card.setPrice(entries ? resolvePrice(product, entries) : null);
      // A product's labelled photos ride the same payload; null means follow
      // the page's rules.
      // Without entries (fetch failed), release any image held for a pending one.
      if (entries) card.setFaceoutOverrides(resolveFaceoutOverrides(product, entries));
      else card.releaseFaceoutWait();
      if (entries) card.setMessaging(resolveMessaging(product, entries));
    }
  }

  //#region - Filter & Sort UI
  /**
   * Per-group value allowlists from the per-collection
   * `collection_filters_to_include` metafield. Each line is a `Group: Value`
   * pair (e.g. `Style: Crewnecks`); pairs fold into a group list with a
   * per-group value allowlist. Groups the metafield doesn't name are not
   * gated — they render in full (the metafield restricts values within named
   * groups, matching the live theme). A line with no colon names a group with
   * no value restriction (a no-op for display) and may be a comma-separated
   * list of such keys. Empty = show all response facets.
   * @returns {SSIncludeGroup[]}
   */
  #includeGroups() {
    const include = config().collectionFiltersToInclude;
    const source = Array.isArray(include) ? include.join('\n') : typeof include === 'string' ? include : '';
    if (!source.trim()) return [];

    /** @type {Map<string, SSIncludeGroup>} */
    const groups = new Map();
    /** @param {string} rawKey @param {string} rawValue */
    const addGroup = (rawKey, rawValue) => {
      const key = rawKey.trim();
      if (!key) return;
      const mapKey = key.toLowerCase();
      let group = groups.get(mapKey);
      if (!group) {
        group = { key, values: [] };
        groups.set(mapKey, group);
      }
      const value = rawValue.trim();
      if (value && !group.values.some((v) => v.toLowerCase() === value.toLowerCase())) {
        group.values.push(value);
      }
    };

    for (const rawLine of source.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line) continue;
      const colon = line.indexOf(':');
      if (colon === -1) {
        for (const token of line.split(',')) addGroup(token, '');
      } else {
        addGroup(line.slice(0, colon), line.slice(colon + 1));
      }
    }
    return Array.from(groups.values());
  }

  #onPanelToggle = (/** @type {Event} */ event) => {
    const open = Boolean(/** @type {CustomEvent} */ (event).detail?.open);
    this.filterLauncher?.setAttribute('aria-expanded', open ? 'true' : 'false');
  };

  #onFilterToggle = (/** @type {Event} */ event) => {
    const { field, value } = /** @type {CustomEvent} */ (event).detail || {};
    if (field && value != null) this.#toggleFilterValue(field, value);
  };

  #onSortChange = (/** @type {Event} */ event) => {
    const { field, direction } = /** @type {CustomEvent} */ (event).detail || {};
    if (field) this.#setSort(field, direction || '');
  };

  #onChipClick = (/** @type {Event} */ event) => {
    const chip = /** @type {HTMLElement} */ (event.target).closest('[data-ss-applied-filter-chip]');
    if (!(chip instanceof HTMLElement)) return;
    const { field, value } = chip.dataset;
    if (field && value != null) this.#toggleFilterValue(field, value);
  };

  #onPromotedFilterClick = (/** @type {Event} */ event) => {
    const control = /** @type {HTMLElement} */ (event.currentTarget);
    this.#applyPromotedFilter(control);
  };

  /** @type {{ x: number, y: number } | null} */
  #promotedFilterPressPoint = null;

  #onPromotedFilterPointerDown = (/** @type {PointerEvent} */ event) => {
    this.#promotedFilterPressPoint = { x: event.clientX, y: event.clientY };
  };

  /**
   * Recover promoted-filter card clicks swallowed by the slideshow's drag
   * handling. When the slideshow takes pointer capture mid-click, the click
   * retargets to the slideshow element instead of the card, so it bubbles here
   * without hitting a control. If the pointer barely travelled (below the
   * slideshow's own 10px swipe threshold), treat it as a click on the control
   * under the release point. Real drags travel further and are ignored.
   */
  #recoverPromotedFilterClick = (/** @type {MouseEvent} */ event) => {
    const pressPoint = this.#promotedFilterPressPoint;
    this.#promotedFilterPressPoint = null;

    if (!event.detail || !pressPoint) return;
    if (event.target instanceof Element && event.target.closest('[data-ss-promoted-filter]')) return;
    if (Math.hypot(event.clientX - pressPoint.x, event.clientY - pressPoint.y) > 8) return;

    const control = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-ss-promoted-filter]');
    if (!(control instanceof HTMLElement)) return;

    this.#applyPromotedFilter(control);
  };

  /**
   * Clone the editable visual promoted-filters section's inert
   * `<template data-ss-promoted-filters-template>` (emitted by the
   * ts-promoted-filters section) into the header slot inside this component, so
   * the promoted-filter wiring below (click → facet toggle, arrows, active
   * state) treats its cards like the server-rendered controls. One-time at
   * connect; the card content is static.
   */
  #insertPromotedFilters() {
    const slot = this.querySelector('[data-ss-promoted-filters-slot]');
    const template = /** @type {HTMLTemplateElement | null} */ (
      document.querySelector('template[data-ss-promoted-filters-template]')
    );
    if (!slot || !template) return;
    slot.replaceChildren(template.content.cloneNode(true));
  }

  /**
   * Reflect the active state on the promoted-filter controls after a search:
   * an active control is pressed. Mirrors the applied state the panel/chips
   * show for the same field/value.
   */
  #syncPromotedFilters() {
    for (const control of this.promotedFilters || []) {
      const { field, value } = control.dataset;
      const active = Boolean(field && value != null && (this.#activeFilters[field] || []).includes(value));
      control.setAttribute('aria-pressed', active ? 'true' : 'false');
      control.classList.toggle('ss-promoted-filter--active', active);
    }
  }

  /**
   * Toggle a promoted-filter control. The visual cards (.ss-promoted-filter-card,
   * from the ts-promoted-filters section) are mutually exclusive: activating one
   * first drops every other card's value from the active set, so at most one is
   * applied at a time. The simple chips stay independent multi-select toggles,
   * and deselection is a plain toggle off for both.
   * @param {HTMLElement} control
   */
  #applyPromotedFilter(control) {
    const { field, value } = control.dataset;
    if (!field || value == null) return;

    const isCard = control.classList.contains('ss-promoted-filter-card');
    const activating = !(this.#activeFilters[field] || []).includes(value);
    if (isCard && activating) {
      for (const other of this.promotedFilters || []) {
        if (other === control || !other.classList.contains('ss-promoted-filter-card')) continue;
        const { field: otherField, value: otherValue } = other.dataset;
        if (!otherField || otherValue == null) continue;
        const remaining = (this.#activeFilters[otherField] || []).filter((v) => v !== otherValue);
        if (remaining.length) {
          this.#activeFilters[otherField] = remaining;
        } else {
          delete this.#activeFilters[otherField];
        }
      }
    }

    this.#toggleFilterValue(field, value);
  }

  /**
   * Add or remove a facet value from the active filter set, then re-search.
   * @param {string} field
   * @param {string} value
   */
  #toggleFilterValue(field, value) {
    const current = this.#activeFilters[field] ? [...this.#activeFilters[field]] : [];
    const index = current.indexOf(value);
    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(value);
    }

    if (current.length) {
      this.#activeFilters[field] = current;
    } else {
      delete this.#activeFilters[field];
    }

    this.#currentPage = 1;
    this.#firstLoadedPage = 1;
    this.#updateHash();
    this.#scheduleSearch();
  }

  /**
   * Set the active sort, then re-search.
   * @param {string} field
   * @param {string} direction
   */
  #setSort(field, direction) {
    this.#sortField = field;
    this.#sortDirection = direction;
    this.#currentPage = 1;
    this.#firstLoadedPage = 1;
    this.#updateHash();
    this.#scheduleSearch();
  }

  /** Clear all active filters (sort is preserved), then re-search. */
  #clearAllFilters() {
    if (!Object.keys(this.#activeFilters).length) return;
    this.#activeFilters = {};
    this.#currentPage = 1;
    this.#firstLoadedPage = 1;
    this.#updateHash();
    this.#scheduleSearch();
  }

  /**
   * Debounce the search so rapid facet/sort toggles coalesce into one request.
   * State + URL hash are updated synchronously by the caller; only the network
   * search + render is deferred (in-flight requests are aborted in fetch).
   */
  #scheduleSearch() {
    this.#searchGeneration += 1;
    this.#clearDepthSkeleton();
    if (this.#searchTimer) clearTimeout(this.#searchTimer);
    this.#searchTimer = setTimeout(() => {
      this.#searchTimer = null;
      this.#applySearch();
    }, SEARCH_DEBOUNCE_MS);
  }

  async #applySearch() {
    this.#isRefreshing = true;
    const status = await this.fetchSearchResults();
    if (status === 'success') {
      this.#allResults = this.#response?.results || [];
      await this.#renderProducts();
      this.#syncFilterUI();
    }
    this.#isRefreshing = false;
  }

  /** Refresh the panel (sort + facets) and the applied-filters row from state. */
  #syncFilterUI() {
    this.filterPanel?.update({
      facets: this.#response?.facets || [],
      sortOptions: this.#response?.sorting?.options || [],
      activeFilters: this.#activeFilters,
      sortField: this.#sortField,
      sortDirection: this.#sortDirection,
      includeGroups: this.#includeGroups(),
    });
    this.#renderAppliedFilters();
    this.#syncPromotedFilters();
  }

  /**
   * Resolve a facet value's display label from the response (falls back to the raw value).
   * @param {string} field
   * @param {string} value
   * @returns {string}
   */
  #facetValueLabel(field, value) {
    const facet = (this.#response?.facets || []).find((f) => f.field === field);
    const match = facet?.values.find((v) => v.value === value);
    return match?.label || value;
  }

  /** Render the applied-filters row: a removable chip per active value. */
  #renderAppliedFilters() {
    if (!this.appliedFilters || !this.appliedFiltersList || !this.appliedChipTemplate) return;

    const removeLabel = /** @type {HTMLElement} */ (this.appliedFiltersList).dataset.removeLabel || 'Remove';

    /** @type {DocumentFragment[]} */
    const chips = [];
    for (const [field, values] of Object.entries(this.#activeFilters)) {
      for (const value of values) {
        const fragment = document.importNode(this.appliedChipTemplate.content, true);
        const button = /** @type {HTMLElement | null} */ (fragment.querySelector('[data-ss-applied-filter-chip]'));
        const label = /** @type {HTMLElement | null} */ (fragment.querySelector('[data-ss-chip-label]'));
        if (!button || !label) continue;
        const text = this.#facetValueLabel(field, value);
        button.dataset.field = field;
        button.dataset.value = value;
        button.setAttribute('aria-label', `${removeLabel} ${text}`);
        label.textContent = text;
        chips.push(fragment);
      }
    }

    this.appliedFiltersList.replaceChildren(...chips);
    this.appliedFilters.classList.toggle('ss-hidden', chips.length === 0);
  }
  //#endregion

  //#region - URL hash
  /**
   * Parse the URL into active filters, sort, and page.
   * Hash format: #/filter.color=Blue/filter.size=M/sort.price=asc/page=2
   *
   * A `?page=N` query param (Shopify's native pagination, emitted by the
   * server-rendered next-page link) sets the first loaded page too, so the
   * live results match the page Liquid rendered. A hash `page=` wins over it.
   * Both are clamped to >= 1 here; the upper bound is only known once the
   * response arrives (see #fetchFirstPage).
   */
  #parseHash() {
    // Reset before the early return: a popstate back to a hashless URL must
    // drop the filters/sort the previous hash had applied.
    this.#activeFilters = {};
    this.#sortField = '';
    this.#sortDirection = '';
    this.#currentPage = parsePageParam(new URLSearchParams(window.location.search).get('page'));

    const hash = window.location.hash.slice(1);
    if (!hash) return;

    for (const seg of hash.split('/').filter(Boolean)) {
      const [key, value] = seg.split('=');
      if (!key || !value) continue;
      const decoded = decodeURIComponent(value);

      if (key.startsWith('filter.')) {
        const field = key.slice(7);
        (this.#activeFilters[field] ||= []).push(decoded);
      } else if (key.startsWith('sort.')) {
        this.#sortField = key.slice(5);
        this.#sortDirection = decoded;
      } else if (key === 'page') {
        this.#currentPage = parsePageParam(decoded);
      }
    }
  }

  /**
   * Write current state back to the URL hash. The loaded-page depth is
   * deliberately not written — the grid always resumes from its first loaded
   * page — but legacy `page=` hashes are still honored by #parseHash.
   *
   * Every caller has reset to page 1, so a `?page=N` from a server-rendered
   * next-page link is dropped: a reload must not render page N of the
   * unfiltered collection under an active filter.
   */
  #updateHash() {
    const parts = [];
    for (const [field, values] of Object.entries(this.#activeFilters)) {
      for (const v of values) parts.push(`filter.${field}=${encodeURIComponent(v)}`);
    }
    if (this.#sortField && this.#sortDirection) {
      parts.push(`sort.${this.#sortField}=${this.#sortDirection}`);
    }
    const hash = parts.length ? `#/${parts.join('/')}` : '';
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.hash = hash;
    history.pushState(null, '', url.toString());
  }
  //#endregion
}

if (!customElements.get('ss-collection')) {
  customElements.define('ss-collection', SSCollection);
}

export { SSCollection };
